
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, ChargesFeeService, FeeType, IChargeFee, ITournamentEdition, ITournamentCurrencyBalance, SanitizedResponse, TournamentService, TransactionStatus } from "../../../../../services";
import { FormControl, DateTimeInput, DateTimePickerLayout, DateHelper, DateTimePickerSelectionMode, Classname, ChartType, ChartData, ButtonManageRef, TextInput, NumberInput, Dropdown, ObjectHelper, Scheme, MessageSchemesIcons, Transition, StringHelper, useScreenDimension } from "@rivtn/noseur";
import { Utils, Schemes } from "../../../../../utils";
import { Elements } from "../../../../shared/Elements";
import { ChartManager } from "../../../../shared/ChartManager";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import RecentTransactions from "./shared/RecentTransactions";

interface FinanceProps {
    tournamentEdition: ITournamentEdition;
}

function Finance(props: FinanceProps) {

    const { tournamentEdition } = props;
    const { width } = useScreenDimension();
    const chargesFeeService = ChargesFeeService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const earningsChartDataState = React.useState<ChartData>();
    const labels = i18nManager.Labels.host.manage_tournament.finance;
    const activeDialogMutableRoot = React.useRef<ReactDOM.Root>(null);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [tournamentCurrencies, setTournamentCurrencies] = React.useState<ApiResponseData<ITournamentCurrencyBalance> | undefined>({});

    React.useEffect(() => {
        fetchInformations();
        const allocationAmount = searchParams.get("amount");
        const allocationStatus = searchParams.get("allostatus");
        const allocationCurrency = searchParams.get("currency");
        const allocationFailureReason = searchParams.get("error_message");
        if (allocationAmount && allocationStatus && allocationCurrency) {
            Utils.updateSearchParams("amount", null, setSearchParams);
            Utils.updateSearchParams("currency", null, setSearchParams);
            Utils.updateSearchParams("allostatus", null, setSearchParams);
            Utils.updateSearchParams("error_message", null, setSearchParams);
            renderAllocationStatus(allocationAmount, allocationStatus, allocationCurrency, allocationFailureReason);
        }
    }, []);

    return (<div className="earnings">
        <div className="header">
            <div className="queries">
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={labels.date_range} rightContent={"fa fa-calendar"} style={{ width: 360, marginLeft: 0 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformations);
                        }} />
                </FormControl>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
                <span className="link lined-limit" style={{ marginLeft: 20 }} onClick={allocateCapital}>{labels.allocate_capital}</span>
                <span className="link lined-limit" style={{ marginLeft: 20 }}>{labels.export}</span>
                <ReactRouterDom.NavLink to={`http://localhost:3901/hosts/${tournamentEdition.tournament.host.public_id}/tournament/${tournamentEdition.tournament?.external_id}/analytics/earnings`} style={{ margin: "15px 0px", width: "fit-content" }} >
                    <span className="link lined-limit" style={{ marginLeft: 20 }}>{labels.view_detailed_anals}</span>
                </ReactRouterDom.NavLink>
                <ReactRouterDom.NavLink to={`/hosts/${tournamentEdition.tournament.host.public_id}/settings?gtab=HEARNS`} style={{ margin: "15px 0px", width: "fit-content" }} >
                    <span className="link lined-limit" style={{ marginLeft: 20 }}>{labels.configure_host_earnings}</span>
                </ReactRouterDom.NavLink>
            </div>
        </div>
        <div className="currency-cards">
            {!tournamentCurrencies?.content && (Elements.LOADING_STATE2)}
            {tournamentCurrencies?.content?.map((tournamentCurrency) => {
                const currencyName = i18nManager.getCurrencyName(tournamentCurrency.currency_code);
                const currencySign = i18nManager.getCurrencySign(tournamentCurrency.currency_code);
                const currencyCountryFlag = i18nManager.getCurrencyCountryFlag(tournamentCurrency.currency_code);

                return (<div key={tournamentCurrency.external_id} className="currency-card" onClick={() => transferToManagers(tournamentCurrency)}>
                    <img alt={tournamentCurrency.currency_code} src={currencyCountryFlag} />
                    <div className="top">
                        <span className="n lined-limit">{currencyName} ({tournamentCurrency.currency_code})</span>
                        <span className={Classname.build("tr", { "p": tournamentCurrency.percentage_increase > 0, "n": tournamentCurrency.percentage_increase < 0, "b": tournamentCurrency.percentage_increase === 0 })}>
                            {Elements.TREND_ICON}
                            <span className="lined-limit">{tournamentCurrency.percentage_increase} %</span>
                        </span>
                    </div>
                    <span className="value" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <span>
                            {currencySign} {StringHelper.toPresentableMoneyValue(tournamentCurrency.balance_amount)}
                        </span>
                        <span style={{ fontWeight: "normal", fontSize: 13, marginTop: 5 }}>{labels.projected_returns} {currencySign} {StringHelper.toPresentableMoneyValue(tournamentCurrency.projected_return_amount)}</span>
                    </span>
                </div>);
            })}
        </div>
        <span className="hint">{labels.click_to_withdraw} <span>{labels.scroll_for_more}</span></span>
        {(width > 800) && (<ChartManager type={ChartType.LINE} title={labels.earning_chart} chartDataState={earningsChartDataState} />)}
        <RecentTransactions tournamentEdition={tournamentEdition} />
    </div>);

    function fetchInformations() {
        fetchTournamentCurrencies();
        fetchTournamentCurrenciesAnalytics();
    }

    function fetchTournamentCurrencies() {
        setTournamentCurrencies({});
        tournamentService.queryCurrencies(tournamentEdition.tournament.external_id, {
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!,
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth(), false, false)!,
        }).then(({ apiResponse }: SanitizedResponse<ITournamentCurrencyBalance>) => {
            setTournamentCurrencies(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function fetchTournamentCurrenciesAnalytics() {
        earningsChartDataState[1](undefined);
        tournamentService.queryCurrencyAnalytics(tournamentEdition.tournament.external_id, {
            status__eq: TransactionStatus.COMPLETED,
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!,
            "tournament.external_id": tournamentEdition.tournament.external_id,
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth(), false, false)!,
        }).then(({ apiResponse }: SanitizedResponse<any>) => {
            earningsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function allocateCapital() {
        let amount = 0;
        let selectedCurrencyCode = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.allocate_capital;

        function updateAmountsPreview() {
            if (!amount || !selectedCurrencyCode) return;
            activeDialogMutableRoot.current?.render(Elements.LOADING_STATE2);

            chargesFeeService.get({
                sec_actor_type: "HOST",
                actor_type: "TOURNAMENT",
                actor_id: tournamentEdition.external_id,
                type: FeeType.TOURNAMENT_CURRENCY_ALLOCATION,
                sec_actor_id: tournamentEdition.tournament.host.external_id,
            }).then(({ sanitized }: SanitizedResponse<IChargeFee>) => {
                if (!activeDialogMutableRoot.current) return;
                const currencySign = i18nManager.getCurrencySign(selectedCurrencyCode);
                const chargeFeeAmount = chargesFeeService.getFlatAmount(sanitized, amount, selectedCurrencyCode);
                const amountToPay = amount + chargeFeeAmount;
                const chargeFeePercentage = chargesFeeService.getPercentageCharged(amountToPay, amount);
                activeDialogMutableRoot.current.render(<>
                    <span><b>{dialogLabels.amount2}</b> {currencySign} {StringHelper.toPresentableMoneyValue(amount)}</span>
                    <span style={{ marginTop: 10 }}><b>{dialogLabels.charge_fee}</b> {currencySign} {StringHelper.toPresentableMoneyValue(chargeFeeAmount)} ({chargeFeePercentage} %)</span>
                    <span style={{ marginTop: 10 }}><b>{dialogLabels.total_to_pay}</b> {currencySign} {StringHelper.toPresentableMoneyValue(amountToPay)}</span>
                </>);
            }).catch(BaseService.reportError);
        }

        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.allocate,
            content: (<div style={{ marginTop: 15, display: "flex", flexDirection: "column" }}>
                <a href={dialogLabels.all_to_know.location} target="_blank">{dialogLabels.all_to_know.label}</a>
                <span style={{ margin: "15px 0px" }}>{dialogLabels.select_currency}</span>
                <Dropdown fill noIcon scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.currency, }}
                    textInputProps={{ style: { paddingLeft: 15 } }} options={i18nManager.CountriesAndCurrencies} optionMap={{
                        value: "currency_code",
                        label: "{currency_name} ({currency_code})",
                    }} onSelectOption={(option) => {
                        selectedCurrencyCode = option.currency_code;
                        updateAmountsPreview();
                    }} />
                <FormControl scheme={Schemes.OMNEHOST} isFieldset fill style={{ marginTop: 15 }} label={dialogLabels.amount}>
                    <NumberInput scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        amount = parseFloat(v) * 1000000;
                        updateAmountsPreview();
                    }} />
                </FormControl>
                <div style={{ padding: 20, display: "flex", flexDirection: "column", marginTop: 20, borderTop: "1px solid var(--underlineColor)", borderBottom: "1px solid var(--underlineColor)" }} ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(activeDialogMutableRoot, ReactDOM.createRoot(r));
                    updateAmountsPreview();
                }}></div>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                tournamentService.allocateCurrencyBalance(tournamentEdition.tournament.external_id, selectedCurrencyCode, amount).then(({ status, sanitized }: SanitizedResponse<any>) => {
                    if (status === 204) {
                        renderAllocationStatus(("" + amount), "success", selectedCurrencyCode);
                    } else if (sanitized.payment_redirect_url) {
                        window.location.href = sanitized.payment_redirect_url;
                    } else {
                        alert("WTF");
                    };
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function renderAllocationStatus(allocationAmount: string, allocationStatus: string, allocationCurrency: string, allocationFailureReason?: string | null) {
        const isSuccessful = allocationStatus === "success";
        const dialogLabels = labels.dialog.allocation_status;
        allocationFailureReason = allocationFailureReason ?? labels.dialog.unknown_error;
        const iconStyle = {
            fontSize: "5em",
            margin: "40px auto",
            padding: "20px 22px",
            borderRadius: "100%",
            width: "fit-content",
            border: "6px solid red",
        };

        const dialog = ConfirmDialog({
            style: { maxWidth: 460 },
            onCancel: allocateCapital,
            confirmLabel: i18nManager.Labels.common.close,
            confirmScheme: (isSuccessful ? Schemes.OMNEHOST : Scheme.DANGER),
            cancelLabel: (!isSuccessful ? i18nManager.Labels.common.try_again : undefined),
            title: (isSuccessful ? dialogLabels.success_title : dialogLabels.failed_title),
            content: (<div style={{ display: "flex", flexDirection: "column", marginBottom: 30 }}>
                {isSuccessful
                    ? (<>
                        <i className={Classname.build(MessageSchemesIcons.SUCCESS, Transition.ZOOM_IN)} style={{ ...iconStyle, color: "var(--mainColor)", borderColor: "var(--mainColor)" }} />
                        <span dangerouslySetInnerHTML={{
                            __html: dialogLabels.success_desc
                                .replace("${currency_sign}", i18nManager.getCurrencySign(allocationCurrency))
                                .replace("${amount}", StringHelper.toPresentableMoneyValue(parseInt(allocationAmount)))
                        }} />
                    </>)
                    : (<>
                        <i className={Classname.build(MessageSchemesIcons.INFO, Transition.ZOOM_IN)} style={{ ...iconStyle, color: "#FF7171", borderColor: "#FF7171" }} />
                        <span dangerouslySetInnerHTML={{
                            __html: dialogLabels.failed_desc
                                .replace("${error_message}", allocationFailureReason)
                        }} />
                    </>)}
            </div>),
        });
        dialog.show();
    }

    function transferToManagers(tournamentCurrency: ITournamentCurrencyBalance) {
        let amount = 0;
        let tournamentName = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.dialog.transfer_to_manager;
        const currencySign = i18nManager.getCurrencySign(tournamentCurrency.currency_code);

        function updateEarnersPreview() {
            if (!activeDialogMutableRoot.current) return;

            const receivingMembers = tournamentEdition.tournament.host?.earning_settings?.receiving_member_ids?.map((m) => ({ name: m })) ?? [];
            const amountPerReceiver = (amount / receivingMembers.length);
            const percentagePerReceiver = (100 / receivingMembers.length);
            activeDialogMutableRoot.current.render(<>
                <span>{dialogLabels.who_received_how_much}</span>
                {receivingMembers.map((receivingMember) => (<span style={{ marginTop: 10 }}><b>{receivingMember.name}:</b> {currencySign} {StringHelper.toPresentableMoneyValue(amountPerReceiver)} ({percentagePerReceiver} %)</span>))}
            </>);
        }

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.transfer,
            desc: dialogLabels.desc.replaceAll("${currency_sign}", currencySign).replaceAll("${amount}", StringHelper.toPresentableMoneyValue(tournamentCurrency.balance_amount)),
            content: (<div style={{ marginTop: 15, display: "flex", flexDirection: "column" }}>
                <a href={`/hosts/${tournamentEdition.tournament.host.public_id}/settings?gtab=HEARNS`} target="_blank">{dialogLabels.change_host_earners}</a>
                <span style={{ margin: "15px 0px" }}>{dialogLabels.enter_tour_name}</span>
                <FormControl scheme={Schemes.OMNEHOST} isFieldset fill style={{ marginTop: 15 }} label={dialogLabels.tour_name}>
                    <TextInput scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentName = v;
                    }} />
                </FormControl>
                <FormControl scheme={Schemes.OMNEHOST} isFieldset fill style={{ marginTop: 15 }}
                    label={dialogLabels.amount_max.replaceAll("${currency_sign}", currencySign)
                        .replaceAll("${amount}", StringHelper.toPresentableMoneyValue(tournamentCurrency.projected_return_amount))}>
                    <NumberInput scheme={Schemes.OMNEHOST} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        amount = parseFloat(v) * 1000000;
                        updateEarnersPreview();
                    }} />
                </FormControl>
                <div style={{ padding: 20, display: "flex", flexDirection: "column", marginTop: 20, borderTop: "1px solid var(--underlineColor)", borderBottom: "1px solid var(--underlineColor)" }} ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(activeDialogMutableRoot, ReactDOM.createRoot(r));
                    updateEarnersPreview();
                }}></div>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournamentEdition.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.transferCurrencyBalance(tournamentEdition.tournament.external_id, tournamentCurrency.currency_code, amount).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformations();
                        Siren.alert(dialogLabels.transfer_successful);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Finance;
